import { useMutation } from '@tanstack/react-query';
import { backendServices, CompanyContactType } from 'common';
import { SupplierSignUpData } from './SupplierSignUpSchema';

function applySupplierFormDataToCompanyUserProfile({
    supplierCompanyUserProfile: companyUserProfile,
    supplierFormData,
}: {
    supplierFormData: SupplierSignUpData;
    supplierCompanyUserProfile: backendServices.ViewModels.CompanyUserProfileViewModel;
}): backendServices.ViewModels.CompanyUserProfileViewModel {
    const { business: businessStep, accountAndContact: accountAndContactStep } = supplierFormData;

    const fullName = `${accountAndContactStep.firstName} ${accountAndContactStep.lastName}`.trim();

    const contacts = companyUserProfile.Company.Contacts.map((c) =>
        c.Type === CompanyContactType.Support
            ? {
                  ...c,
                  Name: fullName,
                  Title: accountAndContactStep.title,
                  Phone: accountAndContactStep.phone,

                  Email: accountAndContactStep.loginEmail,
                  Address1: businessStep.address1,
                  Address2: businessStep.address2,
                  Address3: businessStep.address3,
                  City: businessStep.city,
                  State: businessStep.state,
                  Fax: businessStep.fax,
                  PostalCode: businessStep.postalCode,
                  Country: businessStep.country,
              }
            : c
    );

    const primaryContact = companyUserProfile.Company.Contacts.find(
        (c) => c.Type === CompanyContactType.Support
    ) as backendServices.ViewModels.ContactViewModel;

    return {
        ...companyUserProfile,
        Company: {
            ...companyUserProfile.Company,
            Contacts: contacts,
            Name: businessStep.businessName,
            Email: accountAndContactStep.loginEmail,
            Phone: businessStep.phone,
            Address1: businessStep.address1,
            Address2: businessStep.address2 ?? '',
            Address3: businessStep.address3 ?? '',
            City: businessStep.city,
            PostalCode: businessStep.postalCode,
            State: businessStep.state ?? '', // This is optional in the schema
            Country: businessStep.country,
            Fax: businessStep.fax,
            DUNSNumber: businessStep.dunsNumber ?? '',
        },
        User: {
            ...companyUserProfile.User,
            UserName: accountAndContactStep.loginEmail, // password validation only uses first part of email as username
            Email: accountAndContactStep.loginEmail,
            Password: '',
            Name: fullName,
            Phone: accountAndContactStep.phone,
        },
        Profiles: companyUserProfile.Profiles.map((profile) => {
            switch (profile.ProfileType) {
                case backendServices.ViewModels.ProfileType.Invoice: {
                    return {
                        ...profile,
                        Name: businessStep.businessName,
                        InvoiceConfirmationEmail: primaryContact.Email,
                        RemittanceAdviceEmail: primaryContact.Email,
                        Address1: businessStep.address1,
                        Address2: businessStep.address2 ?? '',
                        Address3: businessStep.address3 ?? '',
                        City: businessStep.city,
                        State: businessStep.state ?? '',
                        PostalCode: businessStep.postalCode,
                        Country: businessStep.country,
                        Phone: primaryContact.Phone,
                        Fax: businessStep.fax ?? '',
                        Email: primaryContact.Email,
                    };
                }
                case backendServices.ViewModels.ProfileType.SupplierWarehouse: {
                    return {
                        ...profile,
                        Name: businessStep.businessName,
                        InvoiceConfirmationEmail: primaryContact.Email,
                        RemittanceAdviceEmail: primaryContact.Email,
                        Address1: businessStep.address1,
                        Address2: businessStep.address2 ?? '',
                        Address3: businessStep.address3 ?? '',
                        City: businessStep.city,
                        State: businessStep.state ?? '',
                        PostalCode: businessStep.postalCode,
                        Country: businessStep.country,
                        Phone: primaryContact.Phone,
                        Fax: businessStep.fax ?? '',
                        Email: primaryContact.Email,
                    };
                }
                case backendServices.ViewModels.ProfileType.Sales: {
                    return {
                        ...profile,
                        Name: businessStep.businessName,
                        InvoiceConfirmationEmail: primaryContact.Email,
                        RemittanceAdviceEmail: primaryContact.Email,
                        Address1: businessStep.address1,
                        Address2: businessStep.address2 ?? '',
                        Address3: businessStep.address3 ?? '',
                        City: businessStep.city,
                        State: businessStep.state ?? '',
                        PostalCode: businessStep.postalCode,
                        Country: businessStep.country,
                        Phone: primaryContact.Phone,
                        Fax: businessStep.fax ?? '',
                        Email: primaryContact.Email,
                        // todo: update po delivery email?
                        // ProfileDeliveryMethods: profile.ProfileDeliveryMethods.map((method) => method),
                    };
                }
                default:
                    return profile;
            }
        }),
    };
}

export function usePutSupplierCompany({ onSuccess }: { onSuccess: () => void }) {
    const mutation = useMutation({
        mutationFn: async (args: {
            supplierFormData: SupplierSignUpData;
            supplierCompanyUserProfile: backendServices.ViewModels.CompanyUserProfileViewModel;
        }) => {
            const updatedSupplierCompanyUserProfile = applySupplierFormDataToCompanyUserProfile(args);

            const companyApi = new backendServices.Apis.CompanyApi();

            const res = await companyApi.putCompanyUserProfile({
                CompanyUserProfiles: [updatedSupplierCompanyUserProfile],
            });

            const result = res.data.results[0];

            return result;
        },
        onSuccess: () => {
            onSuccess();
        },
    });

    return mutation;
}
